import classNames from 'classnames';
import { ComponentProps, MutableRefObject, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import Button from '../Button';
import IconButton from '../IconButton';
import styles from './styles/styles.module.scss';

const singleLineHeight = 24; // 16px font-size, 1.5 line-height
const textAreaPadding = 24;

type SubmitInputProps = {
  /** Multiline only: puts the buttons on their own line under the text, instead of beside it. */
  actionsBelow?: boolean;
  autoFocus?: boolean;
  characterLimit?: number;
  className?: string;
  /** Shows a button next to submit that empties the input, once it has text. */
  clearable?: boolean;
  disabled?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  labelText?: string;
  loading?: boolean;
  /** Multiline only: the fewest lines tall the input is, to hint that a longer message fits. It still grows to 5. */
  minRows?: number;
  multiline?: boolean;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  /** Multiline only: Enter submits and Shift+Enter adds a line, the way chat inputs work. */
  submitOnEnter?: boolean;
  submitIcon: ComponentProps<typeof IconButton>['icon'];
  value?: string;
};

export default function SubmitInput({
  actionsBelow = false,
  autoFocus,
  characterLimit,
  className,
  clearable = false,
  disabled,
  inputRef,
  labelText,
  loading,
  minRows = 1,
  multiline = false,
  onChange,
  onSubmit,
  placeholder,
  required = false,
  submitOnEnter = false,
  submitIcon,
  value
}: SubmitInputProps) {
  const textArea = useRef<HTMLTextAreaElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const intl = useIntl();

  const updateRows = () => {
    if (textArea.current) {
      textArea.current.setAttribute('rows', '1');
      const textHeight = textArea.current.scrollHeight - textAreaPadding;
      const rows = Math.min(Math.max(Math.ceil(textHeight / singleLineHeight), minRows), 5);
      textArea.current.setAttribute('rows', String(rows));
    }
  };

  // Resize when the value changes from outside, so it shrinks back when cleared (ex: after a submit).
  useEffect(updateRows, [minRows, value]);

  const overCharacterLimit = Boolean(characterLimit && value && value.length > characterLimit);
  const canSubmit = !disabled && !loading && !overCharacterLimit;

  const inputElement = multiline ? (
    <textarea
      ref={el => {
        textArea.current = el;
        if (inputRef) {
          inputRef.current = el;
        }
      }}
      aria-label={labelText || placeholder}
      autoFocus={autoFocus}
      className={styles.textArea}
      onChange={e => {
        onChange(e.target.value);
        updateRows();
        textArea.current?.scrollIntoView();
      }}
      onKeyDown={e => {
        if (submitOnEnter && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          if (canSubmit) {
            onSubmit(value || '');
          }
        }
      }}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  ) : (
    <input
      ref={el => {
        input.current = el;
        if (inputRef) {
          inputRef.current = el;
        }
      }}
      aria-label={labelText || placeholder}
      autoFocus={autoFocus}
      className={styles.input}
      onChange={e => {
        onChange(e.target.value);
      }}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  );

  return (
    <label {...setDenaliAttribute('SubmitInput')} className={className}>
      {labelText && <span className={styles.labelText}>{labelText}</span>}
      <div className={classNames(styles.inputContainer, overCharacterLimit && styles.hasError, multiline && actionsBelow && styles.actionsBelow)}>
        {inputElement}
        <div className={styles.actions}>
          {clearable && value && (
            <Button
              className={styles.button}
              onClick={() => {
                onChange('');
                (textArea.current ?? input.current)?.focus();
              }}
              size="sm"
              text={intl.formatMessage({ defaultMessage: 'Clear' })}
              type="button"
              variant="ghost"
            />
          )}
          <IconButton
            className={styles.button}
            disabled={disabled || overCharacterLimit}
            icon={submitIcon}
            loading={loading}
            onClick={() => onSubmit(value || '')}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Submit' })}
            type="submit"
            variant="primary"
          />
        </div>
      </div>
      {!disabled && overCharacterLimit && (
        <div className={styles.characterLimitError}>
          <span>
            <FormattedMessage defaultMessage="Character limit exceeded" />
          </span>
          <span>
            {value?.length}/{characterLimit}
          </span>
        </div>
      )}
    </label>
  );
}
